<script setup lang="ts">
import type { PricingTier } from '@@/types/api';
import { formatCurrency } from '~/composables/format-currency';

const props = defineProps<{
  label: string;
  pricing: PricingTier;
}>();

const expanded = ref(false);
const bodyId = useId();

const hasSiteFee = computed(() => Boolean(props.pricing.accommodation_price));

const isFree = computed(
  () => props.pricing.ticket_price === 0 && !props.pricing.accommodation_price,
);

const ticketLabel = computed(() =>
  props.pricing.accommodation_price ? 'Per-person fee' : 'Ticket price',
);

const hasServiceFee = computed(() => props.pricing.service_fee > 0);

const hasBreakdown = computed(
  () => hasSiteFee.value || !isFree.value || hasServiceFee.value,
);

function toggle() {
  if (hasBreakdown.value) {
    expanded.value = !expanded.value;
  }
}
</script>

<template>
  <div class="price-block">
    <button
      v-if="hasBreakdown"
      type="button"
      class="price-block__header d-flex justify-space-between align-center"
      :aria-expanded="expanded"
      :aria-controls="bodyId"
      data-testid="price-block-toggle"
      @click="toggle"
    >
      <span class="text-overline text-medium-emphasis">{{ label }}</span>
      <span class="d-inline-flex align-center">
        <span class="font-weight-bold">{{
          formatCurrency(pricing.total)
        }}</span>
        <v-icon
          class="price-block__caret ml-1"
          :class="{ 'price-block__caret--expanded': expanded }"
        >
          mdi-chevron-down
        </v-icon>
      </span>
    </button>

    <div
      v-else
      class="price-block__header price-block__header--static d-flex justify-space-between align-center"
    >
      <span class="text-overline text-medium-emphasis">{{ label }}</span>
      <span v-if="pricing.total !== 0" class="font-weight-bold">{{
        formatCurrency(pricing.total)
      }}</span>
    </div>

    <v-expand-transition>
      <div v-if="expanded" :id="bodyId">
        <div
          v-if="pricing.accommodation_price"
          class="d-flex justify-space-between text-body-2"
        >
          <span>Site fee</span>
          <span>{{ formatCurrency(pricing.accommodation_price) }}</span>
        </div>
        <div v-if="!isFree" class="d-flex justify-space-between text-body-2">
          <span>{{ ticketLabel }}</span>
          <span>{{ formatCurrency(pricing.ticket_price) }}</span>
        </div>
        <EventsServiceFeeLine
          v-if="pricing.service_fee > 0"
          :amount="pricing.service_fee"
        />
      </div>
    </v-expand-transition>
  </div>
</template>

<style scoped>
.price-block__header {
  width: 100%;
}
button.price-block__header {
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.price-block__caret {
  transition: transform 0.2s ease;
}
.price-block__caret--expanded {
  transform: rotate(180deg);
}
</style>
